import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTIMATE_STATUS_LABELS,
  formatCents,
  formatDate,
  type Customer,
  type Estimate,
  type EstimateStatus,
  type LineItemInput,
} from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ListFoot, usePagedList } from '../components/PagedList';
import { LineItemEditor } from '../components/LineItemEditor';
import { PageFooter } from '../components/Layout';
import {
  ConfirmDeleteModal,
  SelectCell,
  SelectionBar,
  useSelection,
} from '../components/Selection';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  FilterTabs,
  Input,
  Modal,
  PageHeader,
  Select,
  SummaryBand,
  sortRows,
  SortTh,
  Spinner,
  Table,
  Td,
  Th,
  useTableSort,
} from '../components/ui';

const STATUS_TONE: Record<EstimateStatus, 'slate' | 'blue' | 'green' | 'red' | 'amber'> = {
  draft: 'slate',
  sent: 'blue',
  approved: 'green',
  declined: 'red',
  expired: 'amber',
};

const FILTERS: Array<EstimateStatus | 'all'> = [
  'all',
  'draft',
  'sent',
  'approved',
  'declined',
  'expired',
];

export function EstimatesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<EstimateStatus | 'all'>('all');
  const [creating, setCreating] = useState(false);

  const list = usePagedList<Estimate>({
    queryKey: keys.estimates(filter),
    fetchPage: (cursor, limit) =>
      api.estimates.list({ ...(filter === 'all' ? {} : { status: filter }), cursor, limit }),
  });
  const estimates = list.items;
  const isLoading = list.isLoading;

  const sort = useTableSort<'number' | 'title' | 'customer' | 'sent' | 'total' | 'status'>(
    'number',
    'desc',
  );
  const sorted = sortRows(estimates, sort, (estimate: Estimate, key) => {
    switch (key) {
      case 'number':
        return estimate.number;
      case 'title':
        return estimate.title;
      case 'customer':
        return estimate.customer?.name ?? null;
      case 'sent':
        return estimate.sentAt;
      case 'total':
        return (estimate.totals as { totalCents?: number })?.totalCents ?? 0;
      case 'status':
        return ESTIMATE_STATUS_LABELS[estimate.status];
    }
  });

  /* Ticking rows and deleting them — see components/Selection.tsx. */
  const selection = useSelection(sorted);
  const [confirming, setConfirming] = useState(false);
  const qc = useQueryClient();

  const openValue = estimates
    .filter((e) => e.status === 'sent')
    .reduce((sum, e) => sum + ((e.totals as { totalCents?: number })?.totalCents ?? 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Estimates"
        subtitle={
          openValue > 0 ? (
            <>
              <span className="font-semibold">{formatCents(openValue)}</span> awaiting a decision
            </>
          ) : (
            `${estimates.length} estimates`
          )
        }
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            New estimate
          </Button>
        }
        tabs={
          <FilterTabs
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((f) => ({
              value: f,
              label: f === 'all' ? 'All' : ESTIMATE_STATUS_LABELS[f],
            }))}
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto max-w-[1860px]">
          <Card
            title={filter === 'all' ? 'All estimates' : ESTIMATE_STATUS_LABELS[filter]}
            icon="file"
            count={estimates.length}
            divided
            bodyClassName="p-0"
            band={
              openValue > 0 ? (
                <SummaryBand
                  label="Out for approval"
                  caption="Sent, and not yet decided"
                  amount={formatCents(openValue)}
                />
              ) : undefined
            }
          >
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Spinner className="size-5 text-ink-4" />
              </div>
            ) : estimates.length === 0 ? (
              <EmptyState
                title="No estimates here"
                description="Build one from the price book and send it to the customer."
                action={
                  <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                    New estimate
                  </Button>
                }
              />
            ) : (
              <>
                <SelectionBar
                  count={selection.count}
                  one="estimate"
                  many="estimates"
                  onClear={selection.clear}
                  onDelete={() => setConfirming(true)}
                />
                <Table>
                <thead>
                  <tr>
                    {can('admin', 'dispatcher') && (
                      <Th className="w-[44px]">
                        <SelectCell
                          checked={selection.allVisible}
                          indeterminate={selection.someVisible}
                          onChange={selection.toggleAll}
                          label="Select all estimates"
                        />
                      </Th>
                    )}
                    <SortTh sortKey="title" sort={sort} className="w-[34%]">Estimate</SortTh>
                    <SortTh sortKey="customer" sort={sort}>Customer</SortTh>
                    <SortTh sortKey="sent" sort={sort}>Sent</SortTh>
                    <SortTh sortKey="total" sort={sort}>Total</SortTh>
                    <SortTh sortKey="status" sort={sort} align="right">Status</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((estimate: Estimate) => (
                    <tr
                      key={estimate.id}
                      onClick={() => navigate(`/estimates/${estimate.id}`)}
                      className="cursor-pointer transition-colors hover:bg-raised"
                    >
                      {can('admin', 'dispatcher') && (
                        <Td>
                          <SelectCell
                            checked={selection.isSelected(estimate.id)}
                            onChange={() => selection.toggle(estimate.id)}
                            label={`Select ${estimate.id}`}
                          />
                        </Td>
                      )}
                      <Td>
                        <div className="text-[14px] font-semibold text-ink">{estimate.title}</div>
                        <div className="mt-1 text-[12px] text-ink-4">#{estimate.number}</div>
                      </Td>
                      <Td>{estimate.customer?.name}</Td>
                      <Td className="tnum">
                        {estimate.sentAt ? formatDate(estimate.sentAt) : 'Not sent'}
                      </Td>
                      <Td className="tnum">
                        {formatCents((estimate.totals as { totalCents?: number })?.totalCents ?? 0)}
                      </Td>
                      <Td align="right">
                        <Badge tone={STATUS_TONE[estimate.status]}>
                          {ESTIMATE_STATUS_LABELS[estimate.status]}
                        </Badge>
                        {estimate.convertedJobId && (
                          <Badge tone="violet" className="ml-1.5">
                            Job created
                          </Badge>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                </Table>
                <ListFoot list={list} one="estimate" many="estimates" />
            </>
            )}
          </Card>
        </div>

        <PageFooter label={`${estimates.length} estimates`} />
      </div>

      <NewEstimateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(estimate) => navigate(`/estimates/${estimate.id}`)}
      />

      <ConfirmDeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        count={selection.count}
        one="estimate"
        many="estimates"
        warning="An estimate that has already become a job cannot be deleted."
        onConfirm={() => api.estimates.bulkRemove(selection.selected)}
        onDeleted={() => {
          void qc.invalidateQueries({ queryKey: ['estimates'] });
          selection.clear();
        }}
      />

    </>
  );
}

function NewEstimateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (estimate: Estimate) => void;
}) {
  const qc = useQueryClient();
  const { company } = useAuth();
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [title, setTitle] = useState('');
  const [lineItems, setLineItems] = useState<LineItemInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: customers = [] } = useQuery({
    queryKey: keys.customers(search),
    queryFn: () => api.customers.list({ q: search || undefined, limit: 25 }),
    select: (d) => d.items,
    enabled: open,
  });

  const selected = customers.find((c: Customer) => c.id === customerId);
  const properties = selected?.properties ?? [];

  const create = useMutation({
    mutationFn: () =>
      api.estimates.create({
        customerId,
        propertyId: propertyId || properties[0]?.id,
        title: title.trim(),
        lineItems,
      }),
    onSuccess: (estimate) => {
      void qc.invalidateQueries({ queryKey: ['estimates'] });
      onCreated(estimate as Estimate);
    },
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not create'),
  });

  const canSubmit = title.trim() && customerId && (propertyId || properties.length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New estimate"
      width="xl"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create estimate
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Customer" required>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers"
              className="mb-1.5"
            />
            <Select
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setPropertyId('');
              }}
            >
              <option value="">Choose a customer…</option>
              {customers.map((c: Customer) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Address" required>
            <Select
              value={propertyId || properties[0]?.id || ''}
              onChange={(e) => setPropertyId(e.target.value)}
              disabled={!selected}
              className="mt-[38px]"
            >
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.line1}, {p.city}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="What are you quoting?" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Full system replacement — 3 ton heat pump"
          />
        </Field>

        <div className="rounded-lg border border-line p-3">
          <h3 className="mb-2 text-[11px] font-semibold tracking-[0.15em] text-ink-4 uppercase">
            Line items
          </h3>
          <LineItemEditor
            value={lineItems}
            onChange={setLineItems}
            taxRateBps={company?.taxRateBps ?? 0}
          />
        </div>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}
